import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';
import {
  rollForward,
  projectWealth,
  fireNumber,
  requiredCapital,
  fireCrossover,
  annualEquivalent,
  toKr,
  type LedgerEvent,
  type ProjectionBucket,
} from '@heivol-finance/calc';
import { budgetApi, netWorthApi, projectionApi, seedApi, type BudgetData, type NetWorthData, type ProjectionData } from '../lib/data.js';
import { formatOre } from '../lib/format.js';
import { chartColors } from '../lib/chart.js';
import { PageHeader, StatCard, Button, EmptyState } from '../components/ui.js';

function monthIndex(dated: string, startYear: number, startMonth: number): number {
  const parts = dated.split('-');
  const y = Number(parts[0] ?? startYear);
  const m = Number(parts[1] ?? 1);
  return (y - startYear) * 12 + (m - startMonth);
}

export function Dashboard(): JSX.Element {
  const [budget, setBudget] = useState<BudgetData | null>(null);
  const [nw, setNw] = useState<NetWorthData | null>(null);
  const [proj, setProj] = useState<ProjectionData | null>(null);
  const [seeding, setSeeding] = useState(false);

  const load = () =>
    Promise.all([budgetApi.load(), netWorthApi.load(), projectionApi.load()]).then(([b, n, p]) => {
      setBudget(b);
      setNw(n);
      setProj(p);
    });
  useEffect(() => {
    void load();
  }, []);

  const colors = useMemo(() => chartColors(), [budget]);

  const model = useMemo(() => {
    if (!budget || !nw || !proj) return null;
    const baseline = budget.summary.netWorthBaseline;
    const st = nw.settings;

    // Net-worth roll-forward for the sparkline + "now".
    const events: LedgerEvent[] = nw.events.map((e) => ({
      month: monthIndex(e.dated, st.startYear, st.startMonth),
      label: e.label,
      amount: e.amount_ore,
      classification: e.classification,
    }));
    const points = rollForward({
      openingNetWorth: st.openingNetWorthOre,
      months: st.horizonMonths,
      monthlyBudgetNet: baseline,
      events: events.filter((e) => e.month >= 0 && e.month < st.horizonMonths),
    });
    const spark = points.filter((_, i) => i % 3 === 0).map((p) => ({ i: p.month, v: toKr(p.accumulated) }));

    // Net worth "now": opening + realized consumptive effects to date.
    const today = new Date().toISOString().slice(0, 10);
    const realized = nw.events
      .filter((e) => e.classification === 'consumptive' && e.dated <= today)
      .reduce((s, e) => s - e.amount_ore, 0);
    const netWorthNow = st.openingNetWorthOre + realized;

    // FIRE projection.
    const sc = proj.scenario;
    const buckets: ProjectionBucket[] = proj.buckets.map((b) => ({
      name: b.name,
      openingValue: b.opening_value_ore,
      annualContribution: b.annual_contribution_ore,
      returnSchedule: b.return_schedule.length ? b.return_schedule : [0.05],
      taxDrag: b.tax_treatment === 'pension' ? 0.153 : 0,
    }));
    const projPoints = projectWealth({
      buckets,
      startAge: sc.start_age,
      startYear: new Date().getFullYear(),
      years: Math.max(1, Math.min(60, Math.max(sc.target_age, 70) - sc.start_age)),
      pensionOpening: sc.pension_opening_ore,
      pensionAnnualAdd: sc.pension_annual_add_ore,
      inflation: sc.inflation,
      real: sc.real_or_nominal === 'real',
    });

    // Required capital: prefer the dedicated retirement budget (annual spending ÷ withdrawal rate);
    // fall back to the current budget's consumptive expenses so the card still works before a
    // retirement budget is entered.
    const annualRetirementSpending = proj.retirementExpenses.reduce(
      (s, e) => s + annualEquivalent(e.planned_ore, e.cadence),
      0,
    );
    const hasRetirementBudget = proj.retirementExpenses.length > 0 && annualRetirementSpending > 0;
    const annualExpenses = hasRetirementBudget
      ? annualRetirementSpending
      : budget.summary.consumptiveMonthly * 12;
    const capital = hasRetirementBudget
      ? requiredCapital(annualRetirementSpending, sc.withdrawal_rate)
      : fireNumber(annualExpenses, sc.withdrawal_rate);
    const fire = fireCrossover(projPoints, capital);

    // Liquidity goal: first year net worth ≥ the target (stored on the scenario).
    const liquidityGoal = sc.liquidity_goal_ore;
    const liquidity = liquidityGoal > 0 ? fireCrossover(projPoints, liquidityGoal).crossover : null;

    const empty =
      budget.incomes.length === 0 &&
      budget.expenses.length === 0 &&
      nw.events.length === 0 &&
      proj.buckets.length === 0;

    return {
      monthlyNet: budget.summary.cashNet,
      baseline,
      netWorthNow,
      spark,
      fire,
      fireTarget: capital,
      hasFireInputs: annualExpenses > 0 && buckets.length > 0,
      hasRetirementBudget,
      liquidityGoal,
      liquidity,
      empty,
    };
  }, [budget, nw, proj]);

  if (!model) return <PageHeader title="Overview" subtitle="Loading…" />;

  const seed = async () => {
    setSeeding(true);
    try {
      await seedApi.run();
      await load();
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Overview"
        subtitle="Your net worth, your monthly net, and when you can retire — all in one place."
      />

      {model.empty ? (
        <EmptyState>
          <div className="mb-3 text-hv-base text-hv-text">Empty app — build your budget, or load sample data from the spreadsheet.</div>
          <Button onClick={seed} disabled={seeding}>
            {seeding ? 'Loading…' : 'Load sample data'}
          </Button>
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label="Net worth now" value={formatOre(model.netWorthNow)} tone="ai" />
            <StatCard label="Monthly net" value={formatOre(model.monthlyNet)} tone={model.monthlyNet >= 0 ? 'success' : 'warning'} hint="Total after expenses" />
            <StatCard
              label="Retirement possible"
              value={model.fire.crossover ? `Age ${model.fire.crossover.age}` : model.hasFireInputs ? '—' : 'Missing data'}
              tone={model.fire.crossover ? 'success' : 'default'}
              hint={model.fire.crossover ? `Year ${model.fire.crossover.year}` : 'See Projection'}
            />
            <StatCard
              label="Required capital"
              value={model.hasFireInputs ? formatOre(model.fireTarget) : '—'}
              hint={model.hasRetirementBudget ? 'Retirement budget ÷ withdrawal rate' : 'Annual expenses ÷ withdrawal rate'}
            />
            {model.liquidityGoal > 0 && (
              <StatCard
                label="Liquidity goal reached"
                value={model.liquidity ? `Age ${model.liquidity.age}` : '—'}
                tone={model.liquidity ? 'success' : 'default'}
                hint={model.liquidity ? `Year ${model.liquidity.year}` : 'Not reached within the horizon'}
              />
            )}
          </div>

          <div className="mt-6 rounded-hv-lg border border-hv-border bg-hv-surface p-4 shadow-hv-sm">
            <div className="mb-2 text-hv-sm font-medium text-hv-text">Net-worth curve</div>
            <div className="h-40 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={model.spark} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
                  <YAxis hide domain={['auto', 'auto']} />
                  <Line type="monotone" dataKey="v" stroke={colors.ai} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-1 text-hv-xs text-hv-text-tertiary">
              From the budget baseline + one-off events. <Link to="/net-worth" className="text-hv-ai-text hover:underline">Open Net Worth →</Link>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <QuickLink to="/budget" label="Budget" desc="Income & expenses" />
            <QuickLink to="/net-worth" label="Net Worth" desc="Accumulated curve" />
            <QuickLink to="/projection" label="Projection" desc="Retirement projection" />
            <QuickLink to="/loan-calculator" label="Loan Calculator" desc="Danish loan rules" />
          </div>
        </>
      )}
    </div>
  );
}

function QuickLink({ to, label, desc }: { to: string; label: string; desc: string }): JSX.Element {
  return (
    <Link
      to={to}
      className="rounded-hv-lg border border-hv-border bg-hv-surface p-4 shadow-hv-sm transition-colors hover:bg-hv-surface-alt"
    >
      <div className="text-hv-sm font-medium text-hv-text">{label}</div>
      <div className="mt-0.5 text-hv-xs text-hv-text-tertiary">{desc}</div>
    </Link>
  );
}
